import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match', {
        description: 'Please make sure your passwords match.'
      });
      return;
    }

    setIsLoading(true);

    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);
    
    if (result.success) {
      toast.success('Account Created Successfully!', {
        description: `Welcome to BOXVERRA, ${formData.first_name}! Check your email for confirmation.`,
        duration: 5000,
        action: {
          label: 'Start Shopping',
          onClick: () => navigate('/shop')
        }
      });
      navigate('/account');
    } else {
      toast.error('Registration Failed', {
        description: result.error || 'Please check your information and try again.'
      });
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center py-16 px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-[#141414] rounded-2xl shadow-xl shadow-[#D4AF37]/10 p-10 border border-[#D4AF37]/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#D4AF37]/30">
              <span className="text-[#0A0A0A] text-3xl font-bold font-heading">B</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2 font-heading">Create Account</h1>
            <p className="text-lg text-[#888888] font-body">Join our exclusive community</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-base font-body text-white">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="mt-2 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-base font-body text-white">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="mt-2 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-base font-body text-white">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#888888]" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="pl-12 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-base font-body text-white">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#888888]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pl-12 pr-12 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-base font-body text-white">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="mt-2 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                placeholder="Confirm your password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] py-7 text-xl font-bold font-body rounded-xl shadow-lg shadow-[#D4AF37]/30 hover:shadow-[#D4AF37]/40 transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#0A0A0A]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </>
              ) : 'Create Account'}
            </Button>
          </form>

          {/* Sign In Link */}
          <p className="mt-8 text-center text-base text-[#888888] font-body">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D4AF37] hover:text-[#B8960C] font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;